// src/services/data.js
// Operaciones de datos sobre la API (PostgreSQL). Los totales y el KPI
// se calculan en el servidor; el frontend solo envía los datos del formulario.
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './api';

const enc = encodeURIComponent;
const qs = (obj) => {
  const p = new URLSearchParams();
  Object.entries(obj || {}).forEach(([k, v]) => { if (v != null && v !== '') p.append(k, v); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

// Sucursales
export const listSucursales = () => apiGet('/data/sucursales');
export const getSucursal = (id) => apiGet(`/data/sucursales/${enc(id)}`);
export const createSucursal = (payload) => apiPost('/data/sucursales', payload);
export const updateSucursal = (id, payload) => apiPut(`/data/sucursales/${enc(id)}`, payload);
export const deleteSucursal = (id) => apiDelete(`/data/sucursales/${enc(id)}`);

// Resumen financiero y cuentas por pagar
export const getResumenFinanciero = (sucursalId, periodo='hoy') => apiGet(`/finanzas/resumen${qs({sucursalId,periodo})}`);
export const guardarReservaApertura = (sucursalId,monto) => apiPut('/finanzas/reserva',{sucursalId,monto});
export const crearCuentaPorPagar = payload => apiPost('/finanzas/cuentas-por-pagar',payload);
export const pagarCuentaPorPagar = (id,monto,archivoUrl='',cuentaBancariaId=null) => apiPost(`/finanzas/cuentas-por-pagar/${enc(id)}/pagar`,{monto,archivoUrl,cuentaBancariaId});
export const registrarDeposito = (sucursalId,payload) => apiPost('/finanzas/depositos',{sucursalId,...payload});
export const anularDeposito = (id) => apiPost(`/finanzas/depositos/${enc(id)}/anular`,{});

// Pagos administrativos
export const listPagosAdministrativos = (filtros) => apiGet(`/pagos-administrativos${qs(filtros)}`);
export const crearPagoAdministrativo = (payload) => apiPost('/pagos-administrativos', payload);
export const anularPagoAdministrativo = (id, motivo) => apiPost(`/pagos-administrativos/${enc(id)}/anular`, { motivo });
// Cuentas por pagar (módulo financiero): listado con indicadores, detalle con pagos, correcciones y anulación.
export const listCuentasPorPagar = (filtros) => apiGet(`/cuentas-por-pagar${qs(filtros)}`);
export const getCuentaPorPagar = (id) => apiGet(`/cuentas-por-pagar/${enc(id)}`);
export const corregirCuentaPorPagar = (id, datos) => apiPut(`/cuentas-por-pagar/${enc(id)}`, datos);
export const anularCuentaPorPagar = (id, motivo) => apiPost(`/cuentas-por-pagar/${enc(id)}/anular`, { motivo });

// Turnos y aperturas
export const listTurnos = () => apiGet('/data/turnos');
export const listAperturasActivas = () => apiGet('/data/aperturas/activas');
export const getEstadoCaja = () => apiGet('/data/caja/estado');
export const abrirTurno = (payload) => apiPost('/data/aperturas', payload);

export const getCajaEstado = (sucursalId) => apiGet(`/data/caja/estado${qs({ sucursalId })}`);

// Cierres (cuadres)
export const listCierres = (filtros) => apiGet(`/data/cierres${qs(filtros)}`);
// Marcar como revisado: documenta el resultado (faltante/sobrante) con una observación; no cambia la diferencia.
export const revisarCierre = (id, comentario) => apiPost(`/data/cierres/${enc(id)}/revisar`, { comentario });
export const getCierre = (id) => apiGet(`/data/cierres/${enc(id)}`);
export const createCierre = (payload) => apiPost('/data/cierres', payload);
export const updateCierre = (id, payload) => apiPut(`/data/cierres/${enc(id)}`, payload);
export const patchCierreFaltante = (id, faltantePagado) =>
  apiPatch(`/data/cierres/${enc(id)}/faltante`, { faltantePagado });
// Eliminación definitiva (solo administrador); el motivo queda en la bitácora.
export const deleteCierre = (id, motivo) => apiDelete(`/data/cierres/${enc(id)}`, { motivo });

// Pagos
export const listPagos = (filtros) => apiGet(`/data/pagos${qs(filtros)}`);
export const getPago = (id) => apiGet(`/data/pagos/${enc(id)}`);
export const createPago = (payload) => apiPost('/data/pagos', payload);
export const updatePago = (id, payload) => apiPut(`/data/pagos/${enc(id)}`, payload);
export const deletePago = (id) => apiDelete(`/data/pagos/${enc(id)}`);

// Categorías (listas configurables por ámbito)
export const getCategorias = (scope) => apiGet(`/data/categorias${qs({ scope })}`);
export const saveCategorias = (scope, categorias) => apiPut('/data/categorias', { scope, categorias });

// Comprobantes: se convierte el archivo a base64 y devuelve su URL protegida (UUID aleatorio).
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^;]+;base64,/, ''));
    reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}
export async function subirComprobante(blob, nombre, mime) {
  const dataBase64 = await fileToBase64(blob);
  const r = await apiPost('/data/archivos', {
    nombre: nombre || blob.name || 'comprobante',
    mime: mime || blob.type || 'application/octet-stream',
    dataBase64,
  });
  return r.url;
}

// Usuarios (solo admin)
export const listUsuarios = () => apiGet('/admin/usuarios');
export const createUsuario = (payload) => apiPost('/admin/createUser', payload);
export const updateUsuario = (payload) => apiPost('/admin/updateUser', payload);
// Vista previa del usuario generado (primer nombre + "_" + inicial del primer apellido; el servidor resuelve duplicados).
// Panel de Compras (Administración): mismas compras de Caja, con filtros.
export const listGastosAdmin = (filtros) => apiGet(`/gastos${qs(filtros)}`);
export const listComprasAdmin = (filtros) => apiGet(`/compras${qs(filtros)}`);
export const sugerirUsername = (datos) => apiGet(`/admin/usuarios/sugerir-username${qs(datos)}`);
export const desbloquearUsuario = (uid) => apiPost('/admin/unlockUser', { uid });
export const deleteUsuario = (uid) => apiPost('/admin/deleteUser', { uid });

// Dashboard, agenda y tareas (solo admin)
export const getDashboard = (sucursalId) => apiGet(`/finanzas/dashboard${qs({ sucursalId })}`);
export const getAgenda = (sucursalId, desde, hasta) => apiGet(`/finanzas/agenda${qs({ sucursalId, desde, hasta })}`);
// La sucursal activa va en la URL: en el cuerpo, `sucursalId` es la sucursal de la tarea (null si es para todos).
export const crearTarea = (sucursalId, tarea) => apiPost(`/finanzas/tareas${qs({ sucursalId })}`, { sucursalId, ...tarea });
export const actualizarTarea = (sucursalId, id, tarea) => apiPut(`/finanzas/tareas/${enc(id)}${qs({ sucursalId })}`, { sucursalId, ...tarea });
export const importarTareas = (sucursalId, tareas) => apiPost('/finanzas/tareas/importar', { sucursalId, tareas });
export const completarTarea = (sucursalId, id) => apiPost(`/finanzas/tareas/${enc(id)}/completar`, { sucursalId });
export const reabrirTarea = (sucursalId, id) => apiPost(`/finanzas/tareas/${enc(id)}/reabrir`, { sucursalId });
export const getTareasSeguimiento = (sucursalId) => apiGet(`/finanzas/tareas/seguimiento${qs({ sucursalId })}`);
export const getTareasLista = (sucursalId, desde, hasta, estado = 'pendientes') => apiGet(`/finanzas/tareas/lista${qs({ sucursalId, desde, hasta, estado })}`);
export const actualizarVencimiento = (id, vencimiento) => apiPut(`/finanzas/cuentas-por-pagar/${enc(id)}/vencimiento`, { vencimiento });

// Proveedores (solo admin). Los saldos son de la sucursal activa.
export const listProveedores = (sucursalId) => apiGet(`/proveedores${qs({ sucursalId })}`);
export const getProveedor = (id, sucursalId) => apiGet(`/proveedores/${enc(id)}${qs({ sucursalId })}`);
export const crearProveedor = (payload) => apiPost('/proveedores', payload);
export const actualizarProveedor = (id, payload) => apiPut(`/proveedores/${enc(id)}`, payload);

// Cuadre del cajero (Ventas | Compras | Gastos) y su corrección cuando el admin la habilita.
export const corregirCierre = (id, payload) => apiPut(`/data/cierres/${enc(id)}/correccion`, payload);
export const getTurnoRegistros = (id) => apiGet(`/data/aperturas/${enc(id)}/registros`);
// Guardar avance: borrador del cuadre del turno abierto (no cierra la caja; idempotente).
export const guardarAvanceTurno = (id, borrador) => apiPut(`/data/aperturas/${enc(id)}/borrador`, borrador);
export const guardarCompraTurno = (id, compra) => apiPost(`/data/aperturas/${enc(id)}/compras`, compra);
export const guardarGastoTurno = (id, gasto) => apiPost(`/data/aperturas/${enc(id)}/gastos`, gasto);
export const buscarProveedores = (q) => apiGet(`/proveedores/buscar${qs({ q })}`);
export const resolverProveedor = (datos) => apiPost('/proveedores/resolver', datos);
// Catálogo de productos de compra
export const buscarProductos = (q) => apiGet(`/productos/buscar${qs({ q })}`);
export const crearProducto = (producto) => apiPost('/productos', producto);
export const buscarTiposGasto = (q) => apiGet(`/tipos-gasto/buscar${qs({ q })}`);
export const resolverTipoGasto = (datos) => apiPost('/tipos-gasto/resolver', datos);
export const buscarBeneficiariosGasto = (q) => apiGet(`/beneficiarios-gasto/buscar${qs({ q })}`);
export const resolverBeneficiarioGasto = (datos) => apiPost('/beneficiarios-gasto/resolver', datos);
export const vincularProducto = (compraItemId, productoId) => apiPost('/productos/vincular', { compraItemId, productoId });
export const listProductosAdmin = (q, inactivos) => apiGet(`/productos${qs({ q, inactivos: inactivos ? '1' : undefined })}`);
export const crearProductoCatalogo = (p) => apiPost('/productos/catalogo', p);
export const actualizarProducto = (id, p) => apiPut(`/productos/${enc(id)}`, p);
export const getPendientesProveedor = (id, sucursalId) => apiGet(`/proveedores/${enc(id)}/pendientes${qs({ sucursalId })}`);

// Revisión administrativa de cierres (solo admin)
export const getCierreDetalle = (id) => apiGet(`/data/cierres/${enc(id)}/detalle`);
export const registrarDatosSistema = (id, datos) => apiPatch(`/data/cierres/${enc(id)}/sistema`, datos);
export const corregirVentasCajero = (id, datos) => apiPatch(`/data/cierres/${enc(id)}/ventas-cajero`, datos);
export const habilitarCorreccion = (id, motivo) => apiPost(`/data/cierres/${enc(id)}/habilitar-correccion`, { motivo });
// Solicitudes de corrección: el cajero la pide; el administrador aprueba o rechaza.
export const solicitarCorreccion = (id, motivo) => apiPost(`/data/cierres/${enc(id)}/solicitar-correccion`, { motivo });
export const listSolicitudesCorreccion = (sucursalId, estado) => apiGet(`/data/solicitudes-correccion${qs({ sucursalId, estado })}`);
export const responderSolicitudCorreccion = (id, accion, comentario) => apiPost(`/data/solicitudes-correccion/${enc(id)}/${accion}`, { comentario });
export const listAperturasAbiertas = (sucursalId) => apiGet(`/data/aperturas/abiertas${qs({ sucursalId })}`);
export const anularApertura = (id, motivo) => apiPost(`/data/aperturas/${enc(id)}/anular`, { motivo });

// Logo de sucursal: el servidor valida tipo real (JPG/PNG/WEBP) y tamaño (5 MB) y devuelve la URL.
export async function subirLogoSucursal(file) {
  const r = await apiPost('/data/sucursales/logo', { dataBase64: await fileToBase64(file) });
  return r.url;
}

// Liquidación del efectivo de cada cierre y reporte de cierres (solo admin)
export const getLiquidacion = (cierreId) => apiGet(`/liquidaciones/cierre/${enc(cierreId)}`);
export const registrarLiquidacion = (cierreId, mov) => apiPost(`/liquidaciones/cierre/${enc(cierreId)}`, mov);
export const anularLiquidacion = (id, motivo) => apiPost(`/liquidaciones/${enc(id)}/anular`, { motivo });
export const guardarInstrucciones = (cierreId, instrucciones) => apiPut(`/liquidaciones/cierre/${enc(cierreId)}/instrucciones`, { instrucciones });
export const realizarInstruccion = (id, datos) => apiPost(`/liquidaciones/instrucciones/${enc(id)}/realizar`, datos);
export const noRealizarInstruccion = (id, motivo) => apiPost(`/liquidaciones/instrucciones/${enc(id)}/no-realizado`, { motivo });
export const cambiarDestinoInstruccion = (id, datos) => apiPost(`/liquidaciones/instrucciones/${enc(id)}/cambiar-destino`, datos);
export const anularInstruccion = (id, motivo) => apiPost(`/liquidaciones/instrucciones/${enc(id)}/anular`, { motivo });
// Comprobantes de una instrucción: varios por instrucción; quitar/reemplazar los deja en el historial.
export const agregarComprobantesInstruccion = (id, datos) => apiPost(`/liquidaciones/instrucciones/${enc(id)}/comprobantes`, datos);
export const eliminarComprobanteInstruccion = (id, motivo) => apiPost(`/liquidaciones/comprobantes/${enc(id)}/eliminar`, { motivo });
export const reemplazarComprobanteInstruccion = (id, datos) => apiPost(`/liquidaciones/comprobantes/${enc(id)}/reemplazar`, datos);
export const listCuentasBancarias = (sucursalId) => apiGet(`/liquidaciones/cuentas-bancarias${qs({ sucursalId })}`);
export const crearCuentaBancaria = (cuenta) => apiPost('/liquidaciones/cuentas-bancarias', cuenta);
export const listCuentasProveedor = (proveedorId) => apiGet(`/liquidaciones/proveedores/${enc(proveedorId)}/cuentas`);
export const crearCuentaProveedor = (proveedorId, cuenta) => apiPost(`/liquidaciones/proveedores/${enc(proveedorId)}/cuentas`, cuenta);
export const getEstadosLiquidacion = (sucursalId, desde, hasta) => apiGet(`/liquidaciones/estados${qs({ sucursalId, desde, hasta })}`);
export const getReporteLiquidacion = (filtros) => apiGet(`/liquidaciones/reporte${qs(filtros)}`);
export const getResultados = (filtros) => apiGet(`/resultados${qs(filtros)}`);
export const getReportePagos = (filtros) => apiGet(`/reportes/pagos${qs(filtros)}`);
export const getReporteComprasProductos = (filtros) => apiGet(`/reportes/compras-productos${qs(filtros)}`);
export const getReporteCuentasPorPagar = (filtros) => apiGet(`/reportes/cuentas-por-pagar${qs(filtros)}`);
export const getReporteProveedores = (filtros) => apiGet(`/reportes/proveedores${qs(filtros)}`);

// Bancos (solo admin): cuentas con saldo y movimientos
export const getCuentasBanco = (filtros) => apiGet(`/bancos/cuentas${qs(filtros)}`);
export const crearCuentaBanco = (cuenta) => apiPost('/bancos/cuentas', cuenta);
export const actualizarCuentaBanco = (id, cuenta) => apiPut(`/bancos/cuentas/${enc(id)}`, cuenta);
export const getMovimientosBanco = (filtros) => apiGet(`/bancos/movimientos${qs(filtros)}`);
export const crearMovimientoBanco = (mov) => apiPost('/bancos/movimientos', mov);
export const anularMovimientoBanco = (id, motivo) => apiPost(`/bancos/movimientos/${enc(id)}/anular`, { motivo });
export const getCierresBancarios = (filtros) => apiGet(`/bancos/cierres${qs(filtros)}`);
export const previewCierreBancario = (cuentaId, mes) => apiGet(`/bancos/cierres/preview${qs({ cuentaId, mes })}`);
export const cerrarMesBancario = (datos) => apiPost('/bancos/cierres', datos);

// Entre taquerías: salidas de productos (no inventario), cuentas entre sucursales y sus pagos
export const listTaquerias = () => apiGet('/entre-sucursales/catalogo');
export const listSalidasTurno = (aperturaId) => apiGet(`/entre-sucursales/salidas${qs({ aperturaId })}`);
export const crearSalidaProductos = (salida) => apiPost('/entre-sucursales/salidas', salida);
export const editarSalidaProductos = (id, salida) => apiPut(`/entre-sucursales/salidas/${enc(id)}`, salida);
export const anularSalidaProductos = (id, motivo) => apiPost(`/entre-sucursales/salidas/${enc(id)}/anular`, { motivo });
// Valor pendiente (Administración): salidas con productos sin valor y completar su valor total.
export const listSalidasPendientesValor = (sucursalId) => apiGet(`/entre-sucursales/salidas-pendientes-valor${qs({ sucursalId })}`);
// Corrección administrativa de una salida (nombres, cantidades, descripciones y valores; recalcula la deuda).
export const corregirSalidaProductos = (id, datos) => apiPut(`/entre-sucursales/salidas/${enc(id)}/correccion`, datos);
export const valorarSalidaProductos = (id, valores) => apiPost(`/entre-sucursales/salidas/${enc(id)}/valorar`, { valores });
export const getCuentasEntreSucursales = (sucursalId) => apiGet(`/entre-sucursales/cuentas${qs({ sucursalId })}`);
export const getCuentaEntreSucursales = (id) => apiGet(`/entre-sucursales/cuentas/${enc(id)}`);
export const registrarPagoEntreSucursales = (id, pago) => apiPost(`/entre-sucursales/cuentas/${enc(id)}/pagos`, pago);
export const editarCompraTurno = (id, compraId, compra) => apiPut(`/data/aperturas/${enc(id)}/compras/${enc(compraId)}`, compra);
export const eliminarCompraTurno = (id, compraId) => apiDelete(`/data/aperturas/${enc(id)}/compras/${enc(compraId)}`);
export const editarGastoTurno = (id, gastoId, gasto) => apiPut(`/data/aperturas/${enc(id)}/gastos/${enc(gastoId)}`, gasto);
export const eliminarGastoTurno = (id, gastoId) => apiDelete(`/data/aperturas/${enc(id)}/gastos/${enc(gastoId)}`);
export const getReporteComprasDuena = (filtros) => apiGet(`/reportes/compras-duena${qs(filtros)}`);
